import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { logger } from '@/utils/logger';
import { brandsApi, clientsApi } from '../lib/api';

const BrandsContext = createContext();

// Cache TTL: 1 hour (in milliseconds)
const CACHE_TTL = 60 * 60 * 1000;
const BRANDS_CACHE_KEY = 'brands_cache_v2'; // v2: brands now carry `currency`
const BRANDS_TIMESTAMP_KEY = 'brands_cache_timestamp_v2';
const CLIENTS_CACHE_KEY = 'clients_cache';
const CLIENTS_TIMESTAMP_KEY = 'clients_cache_timestamp';

export const BrandsProvider = ({ children }) => {
  const [brands, setBrands] = useState(() => {
    // Initialize from cache for faster initial load
    const cachedBrands = localStorage.getItem(BRANDS_CACHE_KEY);
    const timestamp = localStorage.getItem(BRANDS_TIMESTAMP_KEY);
    
    if (cachedBrands && timestamp) {
      const age = Date.now() - parseInt(timestamp, 10);
      if (age < CACHE_TTL) {
        return JSON.parse(cachedBrands);
      }
    }
    return [];
  });

  const [clients, setClients] = useState(() => {
    // Initialize from cache for faster initial load
    const cachedClients = localStorage.getItem(CLIENTS_CACHE_KEY);
    const timestamp = localStorage.getItem(CLIENTS_TIMESTAMP_KEY);
    
    if (cachedClients && timestamp) {
      const age = Date.now() - parseInt(timestamp, 10);
      if (age < CACHE_TTL) {
        return JSON.parse(cachedClients);
      }
    }
    return [];
  });

  const [loading, setLoading] = useState(true);

  // Check if cache is valid
  const isCacheValid = useCallback(() => {
    const brandsTimestamp = localStorage.getItem(BRANDS_TIMESTAMP_KEY);
    const clientsTimestamp = localStorage.getItem(CLIENTS_TIMESTAMP_KEY);
    
    if (!brandsTimestamp || !clientsTimestamp) return false;
    
    const brandsAge = Date.now() - parseInt(brandsTimestamp, 10);
    const clientsAge = Date.now() - parseInt(clientsTimestamp, 10);
    
    return brandsAge < CACHE_TTL && clientsAge < CACHE_TTL;
  }, []);

  useEffect(() => {
    // Only fetch if cache is invalid or user just logged in
    const token = localStorage.getItem('access_token');
    if (!token) {
      setBrands([]);
      setClients([]);
      setLoading(false);
      return;
    }

    // If cache is valid, use it and skip API call
    if (isCacheValid() && brands.length > 0 && clients.length > 0) {
      setLoading(false);
      return;
    }

    // Otherwise fetch fresh data
    fetchBrandsAndClients();
  }, []);

  const fetchBrandsAndClients = async (force = false) => {
    try {
      // Only fetch if user is logged in (has token)
      const token = localStorage.getItem('access_token');
      if (!token) {
        setBrands([]);
        setClients([]);
        clearCache();
        setLoading(false);
        return;
      }

      // Skip if cache is valid and not forcing refresh
      if (!force && isCacheValid() && brands.length > 0 && clients.length > 0) {
        setLoading(false);
        return;
      }

      
      // Fetch both in parallel
      const [brandsResponse, clientsResponse] = await Promise.all([
        brandsApi.getAll({ limit: 1000 }),
        clientsApi.getAll({ limit: 1000 })
      ]);

      const brandsData = brandsResponse.data.data || brandsResponse.data || [];
      const clientsData = clientsResponse.data.data || clientsResponse.data || [];

      setBrands(brandsData);
      setClients(clientsData);

      // Cache with timestamp
      localStorage.setItem(BRANDS_CACHE_KEY, JSON.stringify(brandsData));
      localStorage.setItem(BRANDS_TIMESTAMP_KEY, Date.now().toString());
      localStorage.setItem(CLIENTS_CACHE_KEY, JSON.stringify(clientsData));
      localStorage.setItem(CLIENTS_TIMESTAMP_KEY, Date.now().toString());

    } catch (error) {
      logger.error('[BrandsContext] Failed to fetch:', error);
      
      // Try to load from cache if API fails (even if expired)
      const cachedBrands = localStorage.getItem(BRANDS_CACHE_KEY);
      const cachedClients = localStorage.getItem(CLIENTS_CACHE_KEY);
      
      if (cachedBrands && cachedClients) {
        setBrands(JSON.parse(cachedBrands));
        setClients(JSON.parse(cachedClients));
      } else {
        setBrands([]);
        setClients([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const refetch = useCallback(() => {
    setLoading(true);
    fetchBrandsAndClients(true); // Force refresh
  }, []);

  const clearCache = useCallback(() => {
    localStorage.removeItem(BRANDS_CACHE_KEY);
    localStorage.removeItem(BRANDS_TIMESTAMP_KEY);
    localStorage.removeItem(CLIENTS_CACHE_KEY);
    localStorage.removeItem(CLIENTS_TIMESTAMP_KEY);
    setBrands([]);
    setClients([]);
  }, []);

  // Helper functions
  const getBrandById = useCallback((brandId) => {
    return brands.find(b => b.id === brandId);
  }, [brands]);

  const getClientById = useCallback((clientId) => {
    return clients.find(c => c.id === clientId);
  }, [clients]);

  const getBrandsByClientId = useCallback((clientId) => {
    return brands.filter(b => b.client_id === clientId);
  }, [brands]);

  return (
    <BrandsContext.Provider
      value={{
        brands,
        clients,
        loading,
        refetch,
        clearCache,
        getBrandById,
        getClientById,
        getBrandsByClientId
      }}
    >
      {children}
    </BrandsContext.Provider>
  );
};

export const useBrands = () => {
  const context = useContext(BrandsContext);
  if (!context) {
    throw new Error('useBrands must be used within a BrandsProvider');
  }
  return context;
};
